import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import TableBox from "../TableBox";
import PayoutAmtRow from "./PayoutAmtRow";
import { TableContextProvider, HeadCellType } from "../../interfaces/common";
import { buildHeadCells, formatDollarAmount } from "../../utils/methods";
import { VALIDATION_FIELD_MAPPING } from "../../utils/appConstant";
import useSorting from "../../hooks/useSorting";
import usePagination from "../../hooks/usePagination";
import { SortOrder } from "../../enums";
interface PayoutAmtModalContentProps {
  item: any;
  loading?: boolean;
}

const PayoutAmtModalContent: React.FC<PayoutAmtModalContentProps> = ({ item, loading }) => {
  const { t } = useTranslation();

  const headCells: HeadCellType[] = useMemo(() => buildHeadCells([
    VALIDATION_FIELD_MAPPING.date,
    VALIDATION_FIELD_MAPPING.paymentDetails,
    VALIDATION_FIELD_MAPPING.amount,
  ], 'payoutAmtList'), []);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: SortOrder.desc,
    defaultOrderBy: VALIDATION_FIELD_MAPPING.date,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: 1,
  });

  const values: TableContextProvider = useMemo(() => {
    return {
      rowProps: {},
      row: PayoutAmtRow,
      data: item?.payout_breakdown || item?.transactions || [],
      pagination: false,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total: item?.payout_breakdown?.length || item?.transactions?.length || 0,
      loading: loading || false,
      noDataFoundMessage: t('reconciliation.payout.list.pageTexts.modals.payoutAmt.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
      footerRow: (
        <tr key="footer">
          <td colSpan={headCells.length - 1} className="text-end pe-4 fw-bold">Total Payout Amount</td>
          <td className="fw-bold">{item?.payout_breakdown_total_payout ? `${formatDollarAmount(item.payout_breakdown_total_payout)}` : item?.total_payout ? `${formatDollarAmount(item.total_payout)}` : '-'}</td>
        </tr>
      )
    };
  }, [
    item?.payout_breakdown, item?.payout_breakdown_total_payout, item?.transactions, item?.total_payout, headCells,
    order, orderBy, page, perPage,
    t, setPage, setPerPage, setOrder, setOrderBy,
  ]);

  if (!item) return null;

  return (
    <div className="py-2">
      {/* Property Name */}
      <p className="fw-bold fs-5 mb-2 text-dark">{item.property_name}</p>

      {/* Guest, Date, Status */}
      <div className="d-flex flex-wrap align-items-center mb-4">
        <div className="me-4">
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.guestName')}: </span>
          <span>{item.guest_name}</span>
        </div>
        <div className="me-4">
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.date')}: </span>
          <span>{item.date ? item.date : `${item.check_in_date} - ${item.check_out_date}`}</span>
        </div>
        <div className="me-4">
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.status')}: </span>
          <span>{item.reservation_status_name || item.status}</span>
        </div>
        <div>
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.reservationCode')}: </span>
          <span>{item.reservation_code}</span>
        </div>
      </div>

      {/* Payment Details Table */}
      <TableBox tableValues={values} />

      {/* Verification Status & Notes */}
      {(item.verification_status_name || item.action_name) && (
        <div className="mt-3">
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.verificationStatus')}: </span>
          <span>{item.verification_status_name || item.action_name}</span>
        </div>
      )}
      {item.notes && (
        <div className="mt-2">
          <span className="fw-bold text-dark">{t('reconciliation.payout.list.pageTexts.modals.payoutAmt.notes')}: </span>
          <span>{item.notes}</span>
        </div>
      )}
    </div>
  );
};

export default PayoutAmtModalContent;
